import _ from "lodash";
import * as React from "react";
import "./dropdown.scss";
import classNames from "classnames";
import { SearchOutlined } from "@ant-design/icons";
import { useOutsideAlerter } from "utils/helpers";
import Icon from "atoms/icons/icon";
import { AutoComplete, Input, Select, Tag, type SelectProps } from "antd";
import type { CustomTagProps } from "rc-select/lib/BaseSelect";
import { usePopupParent } from "hooks/usePopupParent";

const { Option } = Select;

export type OptionType = {
  displayName: string;
  value: string;
};

export type OptionSelection = OptionType | OptionType[] | null; // extracted alias

interface Props {
  options: ReadonlyArray<OptionType>;
  placeholder?: string;
  updateHandler: (option: OptionSelection) => void; // using alias
  selectedOption?: OptionSelection; // using alias
  isAutocomplete?: boolean;
  isMultiselect?: boolean;
  defaultValue?: string | string[] | null;
  isFullWidth?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
}

const tagRender = (props: CustomTagProps) => {
  const { label, closable, onClose } = props;
  const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };
  return (
    <Tag
      onMouseDown={onPreventMouseDown}
      closable={closable}
      onClose={onClose}
      style={{ marginRight: 3 }}
    >
      {label}
    </Tag>
  );
};

const DropdownInner: React.FC<Props> = (props) => {
  const {
    options,
    placeholder = "Select a Value",
    updateHandler,
    selectedOption,
    isAutocomplete,
    isMultiselect,
    defaultValue,
    isFullWidth,
    disabled,
    isLoading = false,
  } = props;

  const classes = classNames(["dropdown"]);
  const autocompleteClasses = classNames({
    autocomplete_dropdown: true,
    "autocomplete_dropdown--full-width": isFullWidth,
  });

  const [selectedItem, setSelectedItem] = React.useState<string | null | undefined>(
    Array.isArray(selectedOption) ? null : selectedOption?.displayName ?? null
  );
  const [value, setInputValue] = React.useState<string | undefined>("");
  const getPopupParent = usePopupParent("full-screen");

  // React.useEffect(() => {
  //   if (!Array.isArray(selectedOption) && selectedOption) {
  //     setSelectedItem(selectedOption);
  //     // setInputValue(selectedOption.displayName);
  //   } else {
  //     setSelectedItem(null);
  //     // setInputValue(undefined);
  //   }
  // }, [selectedOption]);


  const handleSelectDropdownItem = React.useCallback(
    (item: OptionType | OptionType[] | null) => {
      if (!Array.isArray(item)) {
        setSelectedItem(item?.displayName);
        setInputValue(item?.displayName);
      }
      updateHandler(item);
    },
    [updateHandler]
  );

  const updatedOptions = options?.map((item) => ({
    label: item.displayName,
    value: item.value,
  }));

  const handleSearch = (val: string) => {
    setInputValue(val);
    setSelectedItem(val);
  };

  if (isMultiselect) {
    return (
      <Select
        getPopupContainer={getPopupParent}
        mode="multiple"
        placeholder={placeholder}
        tagRender={tagRender}
        filterOption={(inputValue, option) => {
          if (!option?.label) return false;
          return option.label
            .toString()
            .toUpperCase()
            .includes(inputValue.toUpperCase());
        }}
        value={
          Array.isArray(selectedOption)
            ? selectedOption.map((o) => o.value)
            : []
        }
        defaultValue={
          Array.isArray(selectedOption)
            ? selectedOption.map((o) => o.value)
            : []
        }
        onChange={(values: string[]) => {
          const selectedItems = options.filter((opt) =>
            values.includes(opt.value)
          );
          handleSelectDropdownItem(selectedItems);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        options={updatedOptions}
      />
    );
  }

  if (isAutocomplete) {
    return (
      <AutoComplete
        getPopupContainer={getPopupParent}
        disabled={disabled}
        data-test-id="dropdown-autocomplete"
        options={updatedOptions}
        filterOption={(inputValue, option) => {
          if (!option) return false;

          const selectOption = selectedOption as OptionType; // single selection
          const selectedValue = value as string;

          // If the selected value matches this option, keep it
          if (selectedValue === selectOption?.value) {
            return true; // always show the selected option
          }
          // Otherwise, do standard filtering
          return option?.label?.toUpperCase()?.includes(selectedValue?.toUpperCase() || "");
        }}
        onSelect={(_, option) => {
          const item = options.find((o) => o.displayName === option?.label);
          if (item) {
            handleSelectDropdownItem(item);
          }
        }}
        value={selectedItem}
        onSearch={handleSearch}
        // defaultValue={defaultValue}
      >
        <Input value={value}
          prefix={<SearchOutlined />}
          suffix={
            <span className="dropdown_icon">
              {isLoading && <Icon isActive faAlias="faSpinner" />}
            </span>
          }
          placeholder={placeholder}
        />
      </AutoComplete>
    );
  }

  return (
    <Select
      className="select"
      getPopupContainer={getPopupParent}
      disabled={disabled}
      value={selectedOption ? (selectedOption as OptionType).value : undefined}
      onChange={(val: string) => {
        const item = _.find(options, (o) => o.value === val);
        if (item) handleSelectDropdownItem(item);
      }}
      onMouseDown={(e) => e.stopPropagation()}
      placeholder={placeholder}
    >
      {options.map((option) => (
        <Option key={option.value} value={option.value}>
          {option.displayName}
        </Option>
      ))}
    </Select>
  );
};

DropdownInner.defaultProps = {
  placeholder: "Select a Value",
  updateHandler: () => {},
};

export default DropdownInner;
